import { http } from '../utils/axios';
import { IOrder } from '../interfaces/order.interface';
import {
  TPaginationResponse,
  IOrdersGetData,
  TProductVariationActionObject,
  TSendOrderLink
} from 'shared/types';
import { getTokenFromLocalStorage } from 'shared/utils/local-storage';
import { IQuestionnaireInstance } from 'shared/interfaces/questionnaire-instance.interface';
import { OrderStatuses } from 'shared/enums';

class OrderService {
  private static instance: OrderService;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): OrderService {
    if (!OrderService.instance) {
      OrderService.instance = new OrderService();
    }

    return OrderService.instance;
  }

  getAll = async (payload: IOrdersGetData) => {
    const { isReport, ...params } = payload;
    const { data } = await http<TPaginationResponse<IOrder>>({
      url: '/orders/affiliate',
      method: 'GET',
      params,
      // Increase timeout for report requests to 60 seconds (1 minute)
      timeout: isReport ? 60000 : 30000
    });

    return data;
  };

  getReport = async (payload: IOrdersGetData) => {
    // Remove pagination parameters for report endpoint (backend limits to 1000 records)
    const { page, limit, ...reportParams } = payload;

    const { data } = await http<TPaginationResponse<IOrder>>({
      url: '/orders/affiliate/report',
      method: 'GET',
      params: reportParams
    });

    return data;
  };

  getOneOrder = async (id: string) => {
    const { data } = await http<IOrder>({
      url: `/orders/${id}`,
      method: 'GET'
    });

    return data;
  };
  createOrder = async (payload: { id: string; newDataCreation: IOrder }) => {
    const { data } = await http<IOrder>({
      url: '/orders',
      method: 'POST',
      data: payload.newDataCreation
    });

    return data;
  };

  updateOrder = async (payload: {
    id: string;
    newData: IOrder | Partial<IOrder>;
  }) => {
    const { data } = await http<IOrder>({
      url: `/orders/${payload.id}`,
      method: 'PUT',
      data: payload.newData
    });

    return data;
  };

  updateOrderProducts = async (payload: {
    id: string;
    productVariations: TProductVariationActionObject[];
  }) => {
    const { data } = await http<{ order: IOrder; errors: string[] }>({
      url: `/orders/${payload.id}/actions/updateProducts`,
      method: 'PUT',
      data: { productVariations: payload.productVariations }
    });

    return data;
  };

  updateOrderStatusToLeaveWaitingRoom = async (payload: { id: string }) => {
    const { data } = await http<IOrder>({
      url: `/orders/${payload}/actions/leaveWaitingRoom`,
      method: 'POST',
      params: { access_token: getTokenFromLocalStorage() }
    });
    return data;
  };

  updateOrderStatus = async (payload: {
    id: string;
    status: OrderStatuses;
  }) => {
    const { data } = await http<IOrder>({
      url: `/orders/${payload.id}/status`,
      method: 'PUT',
      params: { access_token: getTokenFromLocalStorage() },
      data: { status: payload.status }
    });
    return data;
  };

  expediteOrder = async (payload: { id: string; expedited: boolean }) => {
    const { data } = await http<IOrder>({
      url: `/orders/${payload.id}/actions/expedite`,
      method: 'PUT',
      data: { expedited: payload.expedited },
      params: { access_token: getTokenFromLocalStorage() }
    });
    return data;
  };

  cancelOrder = async (payload: { id: string }) => {
    const { data } = await http<IOrder>({
      url: `/orders/${payload.id}/actions/cancel`,
      method: 'POST'
    });
    return data;
  };

  updateQuestionnaireInstance = async (payload: any) => {
    const { data } = await http<IQuestionnaireInstance>({
      method: 'PUT',
      url: `/questionnaireInstances/${payload.id}`,
      data: payload,
      params: { access_token: getTokenFromLocalStorage() }
    });

    return data;
  };

  createJiraServiceTicket = async (payload: {
    orderId: string;
    requestType: string;
  }) => {
    const { data } = await http<{ serviceTicketUrl: string }>({
      method: 'POST',
      url: `/orders/${payload.orderId}/actions/createServiceTicket`,
      data: payload,
      params: { access_token: getTokenFromLocalStorage() }
    });

    return data;
  };

  assignAffiliateAdmin = async (payload: {
    assignedAffiliateAdmin: boolean;
    id: string;
  }) => {
    const { data } = await http<IOrder>({
      url: `/orders/${payload.id}/actions/assignAffiliateAdmin`,
      method: 'PUT',
      data: { assignedAffiliateAdmin: payload.assignedAffiliateAdmin },
      params: { access_token: getTokenFromLocalStorage() }
    });

    return data;
  };

  elevateVisitType = async (id: string) => {
    const { data } = await http<IOrder>({
      url: `/orders/${id}/actions/elevateVisitType`,
      method: 'PUT'
    });

    return data;
  };

  submitOrder = async (id: string) => {
    const { data } = await http<IOrder>({
      url: `/orders/${id}/submit`,
      method: 'POST'
    });

    return data;
  };

  sendToPharmacyRecipients = async (orderIdentifier: string) => {
    const { data } = await http({
      url: '/orders/actions/sendToPharmacyRecipients',
      method: 'POST',
      data: { orderIdentifier }
    });

    return data;
  };

  sendOrderLink = async (payload: TSendOrderLink) => {
    const { data } = await http({
      url: `/orders/${payload.id}/actions/sendOrderLink`,
      method: 'POST',
      data: { inviteType: payload.inviteType }
    });

    return data;
  };

  approveOrder = async (id: string) => {
    const { data } = await http<{ affiliateApprovalDate: Date }>({
      url: `/orders/${id}/actions/approve`,
      method: 'PUT'
    });

    return data;
  };
}

const instance = OrderService.getInstance();

export default instance;
